import type { Command } from './command.type.ts';

/**
 * Command model represents the response from backend command endpoints.
 *
 * Commands are actions that change system state (reindex, send_email, reseed, etc.)
 * following CQRS (Command Query Responsibility Segregation) principles.
 *
 * The backend returns a standardized format via the CommandResponse concern.
 *
 * @example
 * // Inspect a command returned by a tio-data request builder
 * if (command.isSuccess) {
 *   console.log(command.message);
 *   console.log(command.result);
 * } else {
 *   console.error(command.commandErrors);
 * }
 */
export interface CommandExtension extends Command {}
export class CommandExtension {
  /**
   * True when the backend reported status 'success'.
   */
  get isSuccess(): boolean {
    return this.status === 'success';
  }

  /**
   * True when the backend reported status 'error'; pair with `errorMessage` for display.
   */
  get isError(): boolean {
    return this.status === 'error';
  }

  /**
   * True while the backend still reports status 'pending' (queued, not yet run).
   */
  get isPending(): boolean {
    return this.status === 'pending';
  }

  /**
   * Returns true if the command is running
   */
  get isRunning(): boolean {
    return this.status === 'running';
  }

  /**
   * Returns true if the command has any errors
   */
  get hasErrors(): boolean {
    return this.isError || this.commandErrors.length > 0;
  }

  /**
   * Returns array of error detail strings
   */
  get errorMessages(): string[] {
    return this.commandErrors.map((error) => error.detail);
  }

  /**
   * Returns a formatted error message for display
   * If single error, returns just the detail
   * If multiple errors, returns message + list of errors
   */
  get errorMessage(): string {
    if (this.commandErrors.length === 0) {
      return this.message;
    }
    if (this.commandErrors.length === 1) {
      return this.commandErrors[0]?.detail || this.message;
    }
    return `${this.message}: ${this.errorMessages.join(', ')}`;
  }

  /**
   * Returns the execution duration in milliseconds (if available)
   */
  get durationMs(): number | undefined {
    return this.metadata.duration_ms;
  }

  /**
   * Returns the execution duration in seconds (if available)
   */
  get durationSeconds(): number | undefined {
    return this.durationMs ? this.durationMs / 1000 : undefined;
  }

  /**
   * Returns when the command was requested as a Date object
   */
  get requestedAt(): Date | undefined {
    return this.metadata.requested_at ? new Date(this.metadata.requested_at) : undefined;
  }

  /**
   * Returns when the command completed as a Date object (if applicable)
   */
  get completedAt(): Date | undefined {
    return this.metadata.completed_at ? new Date(this.metadata.completed_at) : undefined;
  }
}

const Registration = {
  kind: 'object',
  name: 'command',
  features: CommandExtension,
};
export default Registration;
